import { FileType, ListOrdered } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import type { ExhibitScheme } from "../../caseFiles/types";
import { exhibitSample, ExhibitFormatItems } from "./ExhibitFormatItems";

type ExhibitFormatProps = {
  scheme: ExhibitScheme;
  onSchemeChange: (scheme: ExhibitScheme) => void;
  onCustom: () => void;
  disabled?: boolean;
};

/** Exhibit Format on the Case Files toolbar: the styles in a dropdown. */
export const ExhibitFormatButton: FC<ExhibitFormatProps> = ({
  scheme,
  onSchemeChange,
  onCustom,
  disabled
}) => (
  <DropdownMenu>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-6"
              aria-label={`Exhibit Format: ${exhibitSample(scheme)}`}
              disabled={disabled}
              data-testid="case-files-exhibit-format"
            >
              <ListOrdered className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        Exhibit Format · {exhibitSample(scheme)}
      </TooltipContent>
    </Tooltip>
    <DropdownMenuContent
      align="end"
      className="w-72"
      data-testid="exhibit-format-menu"
    >
      <ExhibitFormatItems
        scheme={scheme}
        onSchemeChange={onSchemeChange}
        onCustom={onCustom}
      />
    </DropdownMenuContent>
  </DropdownMenu>
);

/** Exhibit Format in the "…" menu: the same styles in a submenu. */
export const ExhibitFormatSubmenu: FC<ExhibitFormatProps> = ({
  scheme,
  onSchemeChange,
  onCustom,
  disabled
}) => (
  <DropdownMenuSub>
    <DropdownMenuSubTrigger
      disabled={disabled}
      data-testid="case-files-more-exhibit-format"
    >
      <ListOrdered />
      <span className="flex-1">Exhibit Format</span>
      <span
        className="max-w-24 truncate text-sm text-fg-4 tabular-nums"
        data-testid="case-files-more-exhibit-sample"
      >
        {exhibitSample(scheme)}
      </span>
    </DropdownMenuSubTrigger>
    <DropdownMenuSubContent className="w-72" data-testid="exhibit-format-menu">
      <ExhibitFormatItems
        scheme={scheme}
        onSchemeChange={onSchemeChange}
        onCustom={onCustom}
      />
    </DropdownMenuSubContent>
  </DropdownMenuSub>
);

type OriginalNamesProps = {
  on: boolean;
  onChange: (on: boolean) => void;
  disabled?: boolean;
};

export const ORIGINAL_NAMES_LABEL = "Show Original Names";
const SHORTCUT = "⌥⇧N";

/** Show Original Names on the toolbar: a toggle, pressed while on. */
export const OriginalNamesButton: FC<OriginalNamesProps> = ({
  on,
  onChange,
  disabled
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="inline-flex">
        <Button
          variant="ghost"
          size="icon"
          className={cn("size-6", on && "bg-fill-2 text-fg")}
          aria-label={ORIGINAL_NAMES_LABEL}
          aria-pressed={on}
          aria-keyshortcuts="Alt+Shift+N"
          disabled={disabled}
          onClick={() => onChange(!on)}
          data-testid="case-files-original-names"
        >
          <FileType className="size-3.5" />
        </Button>
      </span>
    </TooltipTrigger>
    <TooltipContent side="bottom">
      {ORIGINAL_NAMES_LABEL} ({SHORTCUT})
    </TooltipContent>
  </Tooltip>
);

/** Show Original Names in the "…" menu: a checkbox item. */
export const OriginalNamesMenuItem: FC<OriginalNamesProps> = ({
  on,
  onChange,
  disabled
}) => (
  <DropdownMenuCheckboxItem
    checked={on}
    disabled={disabled}
    onCheckedChange={(next) => onChange(next === true)}
    data-testid="case-files-more-original-names"
  >
    <FileType />
    {ORIGINAL_NAMES_LABEL}
    <DropdownMenuShortcut className="mr-1">{SHORTCUT}</DropdownMenuShortcut>
  </DropdownMenuCheckboxItem>
);
